import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSortable } from 'react-sortablejs';
import './Level.css';
import '../Animate/animate.css';
import ModalTopic from '../Modal/ModalTopic';
import ModalExam from '../Modal/ModalExam';
import ModalChapter from '../Modal/ModalChapter';
import Loading from '../Common/Loading';
import Toast from '../Common/Toast';
import SavingIndicator from '../Common/SavingIndicator';
import useToast from '../Common/useToast';

import { isAuthenticated, getCurrentUser, isStaff, isAdmin } from '../../api/users_institutions';
import {
  getTopicAdminPermissions, createTopicAdminPermission, deleteTopicAdminPermission,
  getExamAdminPermissions, createExamAdminPermission, deleteExamAdminPermission,
  getChapterAdminPermissions, createChapterAdminPermission, deleteChapterAdminPermission,
  getTestAdminPermissions, createTestAdminPermission, deleteTestAdminPermission,
} from '../../api/admin_permissions';
import { getAllLevels } from '../../api/levels';
import { createTopic, updateTopic, deleteTopic } from '../../api/topics';
import { getTopicContentsByLevel, createTopicContent, updateTopicContent, deleteTopicContent } from '../../api/topics';
import { getChaptersByLevel, createChapter, updateChapter } from '../../api/chapters';
import { fetchTestsByLevel, createTest, updateTest } from '../../api/exams';
import { createExam, updateExam, deleteExam } from '../../api/exams';
import { fetchUserExamAttempts, fetchUserTestAttempts } from '../../api/exams';
import { getLessonsByLevel, getLessonPagesByIds, getUserQuizAttempts } from '../../api/lessons';
import { getCachedLevelSidebarContent, loadLevelSidebarContent, invalidateLevelSidebarContent } from '../../api/levelContentCache';
import { createLevelHistoryLog } from '../../api/history_logs';
import { isLevelNameLockedForUser, checkAdminLevelEditorPermission } from '../../utils/levelAccess';
import { formatApiError } from '../../utils/apiError';
import { createUserTopicContentProgress, updateUserTopicContentProgress, createUserTopicExamProgress, updateUserTopicExamProgress, getUserLevelProgresses, createUserLevelProgress, updateUserLevelProgress, getUserLessonVideoProgresses } from '../../api/progresses'

function Level() {

  const { t, i18n } = useTranslation();
  const textAlignStart = i18n.language === 'ar' ? 'right' : 'left';
  const { levelName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { editorMode: dashboardEditorMode, setActiveLevelId, setSortSessionControls, setLevelViewOnly } = useOutletContext();

  const userIsAuthenticated = isAuthenticated();
  const currentUser = getCurrentUser();
  const userIsStaff = isStaff();
  const userIsAdmin = isAdmin();

  // New: Track current final exam selection
  const [currentExamIndex, setCurrentExamIndex] = useState(null);


  // State for level data
  const [currentLevel, setCurrentLevel] = useState(null);
  const [topics, setTopics] = useState([]);
  const [topicContents, setTopicContents] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [exams, setExams] = useState([]);
  const [tests, setTests] = useState([]);
  // Staged admin-access selections made via the Topic/Exam/Chapter modals this session -- each is
  // a map of { [itemId]: userIds[] } keyed by the topic/exam/chapter/test's own id (temp or
  // real). An entry only exists once the admin actually opens and submits that item's admin
  // access UI; an absent entry means "leave whatever's in the database alone" at Save time,
  // rather than wiping out existing permissions for items nobody touched this session. Part of
  // the same staged session as topics/exams/etc. below (included in buildDataSnapshot), so
  // editing admin access alone still marks the session dirty and participates in undo/redo.
  const [topicAdminAccess, setTopicAdminAccess] = useState({});
  const [examAdminAccess, setExamAdminAccess] = useState({});
  const [chapterAdminAccess, setChapterAdminAccess] = useState({});
  const [testAdminAccess, setTestAdminAccess] = useState({});
  const [userExamAttempts, setUserExamAttempts] = useState([]);
  const [userTestAttempts, setUserTestAttempts] = useState([]);
  const [sidebarRows, setSidebarRows] = useState([]);
  // Lessons (with pages/blocks) belonging to this level's chapters, plus the user's video/quiz
  // progress -- fetched purely to compute UserLevelProgress.progress_percentage across every
  // video block, quiz block, test, and exam in the level (see the sync effect below).
  const [levelLessons, setLevelLessons] = useState([]);
  const [userLessonVideoProgresses, setUserLessonVideoProgresses] = useState([]);
  const [userQuizAttempts, setUserQuizAttempts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animationClass, setAnimationClass] = useState('anim-initial');
  // False while the level-wide chapters/tests/topic-contents/lessons/progress batch (phase 2 of
  // refreshTopicsAndExams below) is still in flight -- guards the progress-sync effects further
  // down so they never compute a prerequisite/completion chain from a partially-loaded snapshot.
  const [levelDataFullyLoaded, setLevelDataFullyLoaded] = useState(false);

  // UI State
  const [currentTopicIndex, setCurrentTopicIndex] = useState(null);
  const [modalTopicAction, setModalTopicAction] = useState(null);
  const [modalExamAction, setModalExamAction] = useState(null);
  const [modalChapterAction, setModalChapterAction] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedExamAttempt, setSelectedExamAttempt] = useState(null);
  const [selectedTestAttempt, setSelectedTestAttempt] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedExamType, setSelectedExamType] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);

  // Level Permission
  // Whether this user may edit this particular level at all -- false for anyone outside an
  // "admins" category (see checkAdminLevelEditorPermission), who still gets to read it. Starts
  // false so the editor UI never flashes on before the check resolves.
  const [adminLevelCanEdit, setAdminLevelCanEdit] = useState(false);
  useEffect(() => {
    if (!levelName || !currentUser?.id) return;
    let cancelled = false;
    checkAdminLevelEditorPermission(levelName, currentUser.id).then((canEdit) => {
      if (!cancelled) setAdminLevelCanEdit(canEdit);
    }).catch((error) => {
      console.error('Error checking level editor permission:', error);
      if (!cancelled) setAdminLevelCanEdit(false);
    });
    return () => { cancelled = true; };
  }, [levelName, currentUser?.id]);

  // A read-only level can't be edited even if the Navbar's editor toggle was left on from
  // elsewhere, so gate the context's flag once here instead of at each of its ~15 use sites
  // below. The Navbar is told separately (viewOnlyMode, reported up right below) so it can drop
  // its Save/Undo/Redo controls and hide the toggle itself.
  const editorMode = dashboardEditorMode && adminLevelCanEdit;

  // Report view-only up to the shared Navbar, which lives in Dashboard.jsx (this component
  // renders into its <Outlet/>, so the outlet context is the only channel to it) -- mirrors the
  // setActiveLevelId / setSortSessionControls effects further down. Reset on unmount so the
  // dashboard grid gets its editor toggle back.
  useEffect(() => {
    if (!setLevelViewOnly) return;
    setLevelViewOnly(!adminLevelCanEdit);
    return () => setLevelViewOnly(false);
  }, [adminLevelCanEdit, setLevelViewOnly]);

  // Scoped to the currently-selected topic -- requires an explicit TopicAdminPermission row for
  // (selectedTopic, currentUser); no permission rows at all for this topic means no access here
  // (unlike LevelAdminPermission's "leave empty for all admins" semantics elsewhere).
  const [selectedTopicAdminPermissions, setSelectedTopicAdminPermissions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!userIsStaff || !selectedTopic) {
      setSelectedTopicAdminPermissions([]);
      return;
    }

    getTopicAdminPermissions().then((permissions) => {
      if (cancelled) return;
      const permissionsForTopic = Array.isArray(permissions)
        ? permissions.filter((permission) => Number(permission.topic?.id ?? permission.topic) === Number(selectedTopic.id))
        : [];
      setSelectedTopicAdminPermissions(permissionsForTopic);
    }).catch((error) => {
      console.error('Error loading topic admin permissions:', error);
      if (!cancelled) setSelectedTopicAdminPermissions([]);
    });
    return () => { cancelled = true; };
  }, [userIsStaff, selectedTopic]);

  // admin permission to edit Topic 
  const canEditSelectedTopicContent = () => {
    const selectedTopicPermission = selectedTopicAdminPermissions.filter((permission) => Number(permission.user?.id ?? permission.user) === Number(currentUser?.id));
    return userIsStaff && (selectedTopicPermission.length > 0 || selectedTopicAdminPermissions.length === 0);
  };

  // Exam Permission
  
  const [selectedExamAdminPermission, setSelectedExamAdminPermission] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!userIsStaff || !selectedExam) {
      setSelectedExamAdminPermission([]);
      return;
    }

    getExamAdminPermissions().then((permissions) => {
      if (cancelled) return;
      const permissionsForExam = Array.isArray(permissions)
        ? permissions.filter((permission) => Number(permission.exam?.id ?? permission.exam) === Number(selectedExam?.id))
        : [];
      setSelectedExamAdminPermission(permissionsForExam);
    }).catch((error) => {
      console.error('Error loading exam admin permissions:', error);
      if (!cancelled) setSelectedExamAdminPermission([]);
    });
    return () => { cancelled = true; };
  }, [userIsStaff, selectedExam]);

  // admin permission to edit Exam
  const canEditSelectedExam = () => {
    const selectedExamPermission = selectedExamAdminPermission.filter((permission) => Number(permission.user?.id ?? permission.user) === Number(currentUser?.id));
    return userIsStaff && (selectedExamPermission.length > 0 || selectedExamAdminPermission.length === 0);
  };

  // Test Permission
  // Unlike exams (one selectedExam shown at a time), multiple test cards can render
  // simultaneously within a topic's content list, so all TestAdminPermission rows are fetched
  // once and filtered per-test on demand rather than tracking a single "selected" test.
  const [allTestAdminPermissions, setAllTestAdminPermissions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!userIsStaff) {
      setAllTestAdminPermissions([]);
      return;
    }
    getTestAdminPermissions().then((permissions) => {
      if (cancelled) return;
      setAllTestAdminPermissions(Array.isArray(permissions) ? permissions : []);
    }).catch((error) => {
      console.error('Error loading test admin permissions:', error);
      if (!cancelled) setAllTestAdminPermissions([]);
    });
    return () => { cancelled = true; };
  }, [userIsStaff]);

  // admin permission to edit a specific Test
  const canEditTest = (testId) => {
    if (!testId) return false;
    const permissionsForTest = allTestAdminPermissions.filter((permission) => Number(permission.test?.id ?? permission.test) === Number(testId));
    const ownPermission = permissionsForTest.filter((permission) => Number(permission.user?.id ?? permission.user) === Number(currentUser?.id));
    return userIsStaff && (ownPermission.length > 0 || permissionsForTest.length === 0);
  };

  // Progress State
  const [userTopicContentProgresses, setUserTopicContentProgresses] = useState([]);
  const [userTopicExamProgresses, setUserTopicExamProgresses] = useState([]);
  const [userLevelProgresses, setUserLevelProgresses] = useState([]);
  const topicContentProgressSyncInProgressRef = useRef(false);
  // SortableJS's fallback drag mode (forceFallback) only swallows the browser's post-drop "ghost"
  // click for a drag that never crosses a valid drop target -- for a real reorder, its internal
  // ignoreNextClick guard gets cleared the moment the pointer first drags over the list, well
  // before the drop. So every real drag ends with a genuine click event landing on whatever
  // sidebar-item/chapter-card is now under the pointer, and those all carry onClick handlers
  // (selecting a topic/exam re-fetches via refreshTopicsAndExams, which doesn't wait for or know
  // about the drag's own pending order-PATCH requests). That stray click's refresh can land after
  // the drag's own refresh and silently overwrite the just-saved order with the pre-drag snapshot
  // -- this ref suppresses those onClick handlers for a brief window right after a drop.
  const suppressClickAfterDragRef = useRef(false);
  const suppressClickAfterDrag = () => {
    suppressClickAfterDragRef.current = true;
    setTimeout(() => { suppressClickAfterDragRef.current = false; }, 300);
  };

  // --- Local editing session: unified undo/redo + explicit Save ---
  // Every edit in this file -- drag-reordering topics/exams/topic-contents, and creating/
  // updating/deleting topics/exams/chapters/tests via the modals below -- only updates local
  // state now. Nothing is sent to the backend until saveLevelChanges() runs (Save button /
  // Ctrl+S). This replaces an earlier fire-and-forget-PATCH-per-drag design that was
  // fundamentally racy: any read that happened to run before a background write actually landed
  // (a fast navigate-away-and-back, or the Dashboard hover-prefetch cache) could show/cache the
  // pre-edit data even though the write itself had succeeded.
  const nextTempIdRef = useRef(0);
  const isTempId = (id) => typeof id === 'string' && id.startsWith('temp-');
  const makeTempId = () => `temp-${++nextTempIdRef.current}`;

  const sessionHistoryRef = useRef([]);
  const sessionHistoryIndexRef = useRef(-1);
  const sessionBaselineRef = useRef(null);
  const hasInitializedSessionRef = useRef(false);
  const isRestoringSessionRef = useRef(false);
  const pendingLogEntriesQueueRef = useRef([]);
  const lastSavedIndexRef = useRef(-1);
  const [sessionHistoryMeta, setSessionHistoryMeta] = useState({ index: -1, length: 0 });
  const [isSavingLevel, setIsSavingLevel] = useState(false);
  // Manual-save feedback: corner spinner while saveLevelChanges() runs, then a toast
  // with the outcome -- replaces the window.alert() this used to block on.
  const { toast, showToast } = useToast();

  const cloneSessionData = (value) => JSON.parse(JSON.stringify(value));
  const buildDataSnapshot = () => ({
    topics, exams, topicContents, chapters, tests,
    topicAdminAccess, examAdminAccess, chapterAdminAccess, testAdminAccess,
  });

  const resetSessionHistory = (dataSnapshot) => {
    const snapshot = cloneSessionData({ ...dataSnapshot, logEntries: [] });
    sessionHistoryRef.current = [snapshot];
    sessionHistoryIndexRef.current = 0;
    sessionBaselineRef.current = cloneSessionData(dataSnapshot);
    lastSavedIndexRef.current = 0;
    pendingLogEntriesQueueRef.current = [];
    setSessionHistoryMeta({ index: 0, length: 1 });
  };

  // A fresh level visit (or switching :levelName) starts a fresh session.
  useEffect(() => {
    hasInitializedSessionRef.current = false;
    sessionHistoryRef.current = [];
    sessionHistoryIndexRef.current = -1;
    sessionBaselineRef.current = null;
    lastSavedIndexRef.current = -1;
    pendingLogEntriesQueueRef.current = [];
    setSessionHistoryMeta({ index: -1, length: 0 });
  }, [levelName]);

  // Establishes the undo/redo baseline once BOTH phases of refreshTopicsAndExams have landed --
  // topicContents/chapters/tests only arrive in phase 2, moments after topics/exams (phase 1), so
  // capturing any earlier would treat phase 2's arrival as one giant fake "edit". Auto-captures
  // every subsequent change to any of the 5 arrays as a new undo-able snapshot -- mirrors
  // Exam.jsx's session-history effect (sessionHistoryRef/applySessionSnapshot/handleUndo/
  // handleRedo), just watching 5 arrays instead of 1. levelDataFullyLoaded is read but
  // deliberately not listed as a dependency: it always flips true in the same commit as
  // topicContents/chapters/tests below, so by the time this effect re-runs off those, it already
  // sees the latest value.
  useEffect(() => {
    if (!levelDataFullyLoaded) return;
    if (!hasInitializedSessionRef.current) {
      hasInitializedSessionRef.current = true;
      resetSessionHistory(buildDataSnapshot());
      return;
    }
    if (isRestoringSessionRef.current) {
      isRestoringSessionRef.current = false;
      return;
    }
    // saveLevelChanges() resolves temp ids to real ones via several setTopics/setExams/
    // setChapters/setTests/etc. calls spread across its many `await`s -- each one can trigger a
    // render (and this effect) with only some entities resolved so far, well before
    // resetSessionHistory() at the end establishes the new post-save baseline. Without this
    // guard, an intermediate render's partially-resolved snapshot gets compared against (and
    // found to mismatch) whatever baseline exists at that moment, auto-capturing a phantom
    // "unsaved edit" -- which is exactly what made the save-status badge and the unsaved-changes
    // warning both keep firing immediately after a successful save with no further edits.
    if (isSavingLevel) return;
    const dataSnapshot = buildDataSnapshot();
    const lastEntry = sessionHistoryRef.current[sessionHistoryIndexRef.current];
    const lastEntryData = lastEntry && {
      topics: lastEntry.topics, exams: lastEntry.exams, topicContents: lastEntry.topicContents,
      chapters: lastEntry.chapters, tests: lastEntry.tests,
      topicAdminAccess: lastEntry.topicAdminAccess, examAdminAccess: lastEntry.examAdminAccess,
      chapterAdminAccess: lastEntry.chapterAdminAccess, testAdminAccess: lastEntry.testAdminAccess,
    };
    if (lastEntryData && JSON.stringify(lastEntryData) === JSON.stringify(dataSnapshot)) return;

    const snapshot = cloneSessionData({
      ...dataSnapshot,
      logEntries: [...(lastEntry?.logEntries || []), ...pendingLogEntriesQueueRef.current],
    });
    pendingLogEntriesQueueRef.current = [];
    sessionHistoryRef.current = sessionHistoryRef.current.slice(0, sessionHistoryIndexRef.current + 1);
    sessionHistoryRef.current.push(snapshot);
    sessionHistoryIndexRef.current = sessionHistoryRef.current.length - 1;
    setSessionHistoryMeta({ index: sessionHistoryIndexRef.current, length: sessionHistoryRef.current.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, exams, topicContents, chapters, tests, topicAdminAccess, examAdminAccess, chapterAdminAccess, testAdminAccess]);

  const applySessionSnapshot = (snapshot) => {
    isRestoringSessionRef.current = true;
    const clone = cloneSessionData(snapshot);
    setTopics(clone.topics);
    setExams(clone.exams);
    setTopicContents(clone.topicContents);
    setChapters(clone.chapters);
    setTests(clone.tests);
    setTopicAdminAccess(clone.topicAdminAccess || {});
    setExamAdminAccess(clone.examAdminAccess || {});
    setChapterAdminAccess(clone.chapterAdminAccess || {});
    setTestAdminAccess(clone.testAdminAccess || {});
    setSidebarRows([...clone.topics, ...clone.exams].sort((a, b) => a.order - b.order));
  };

  const handleUndo = () => {
    if (sessionHistoryIndexRef.current <= 0) return;
    const nextIndex = sessionHistoryIndexRef.current - 1;
    sessionHistoryIndexRef.current = nextIndex;
    setSessionHistoryMeta({ index: nextIndex, length: sessionHistoryRef.current.length });
    applySessionSnapshot(sessionHistoryRef.current[nextIndex]);
  };

  const handleRedo = () => {
    if (sessionHistoryIndexRef.current >= sessionHistoryRef.current.length - 1) return;
    const nextIndex = sessionHistoryIndexRef.current + 1;
    sessionHistoryIndexRef.current = nextIndex;
    setSessionHistoryMeta({ index: nextIndex, length: sessionHistoryRef.current.length });
    applySessionSnapshot(sessionHistoryRef.current[nextIndex]);
  };

  const hasUnsavedChanges = () => sessionHistoryIndexRef.current !== lastSavedIndexRef.current;

  // Shared guard for any action that would leave/unmount this level (back to dashboard, opening
  // a chapter/exam/test) while edits are staged but not yet saved.
  const confirmDiscardUnsavedChanges = () => {
    if (!hasUnsavedChanges()) return true;
    return window.confirm(t('level.unsavedChangesConfirm'));
  };

  const handleNavigateToChapter = (chapterTitle) => {
    if (!confirmDiscardUnsavedChanges()) return;
    setAnimationClass('anim-exit-left');
    setTimeout(() => {
      navigate(`/dashboard/${levelName}/chapters/${chapterTitle}`, { state: { fromDashboard: true } });
    }, 0);
  };

  // Keyboard shortcuts for save/undo/redo while editing -- mirrors Exam.jsx's equivalent.
  // handleUndo/handleRedo/saveLevelChanges are plain functions re-created every render (not
  // memoized), closing over the current topics/exams/etc. -- they must be in the deps so this
  // listener re-attaches with a fresh closure on every render, or Ctrl+S/Z/Y would keep operating
  // on stale state.
  useEffect(() => {
    if (!userIsStaff || !editorMode) return;

    const handleKeyDown = (e) => {
      const activeEl = document.activeElement;
      const isEditingText = activeEl && (activeEl.isContentEditable || ['INPUT', 'TEXTAREA'].includes(activeEl.tagName));
      if (isEditingText) return;

      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      if (isCtrlOrCmd && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        handleUndo();
      } else if (isCtrlOrCmd && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        handleRedo();
      } else if (isCtrlOrCmd && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        saveLevelChanges();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  // Warn before leaving/refreshing the page if there are unsaved staged changes.
  useEffect(() => {
    if (!userIsStaff) return;

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  });

  // Centralized refresh function. Split into two independent phases: phase 1 (topics + exams,
  // plus this user's own UserTopicContentProgress/UserTopicExamProgress records, needed for the
  // sidebar's lock state) is what the sidebar needs, and may already be sitting in the cache
  // Dashboard's level-card hover/focus/touch handler warmed (see levelContentCache.js) -- so it
  // can commit and paint immediately, lock state included. Phase 2 (chapters, tests,
  // topic-contents, lessons+pages, and every other progress/attempt endpoint) is kicked off in
  // parallel, not gated behind phase 1, and commits together once it resolves.
  const refreshTopicsAndExams = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/');
      return;
    }
    try {
      // Resolve the level from nav state when available -- Dashboard already has this exact
      // object at the moment of the click, so this skips fetching every level and searching by
      // name. Falls back to the lookup-by-name for direct URL visits/refreshes/deep links, where
      // no nav state exists.
      let level = location.state?.level;
      if (!level) {
        const levels = await getAllLevels();
        level = levels.find(s => s.name === levelName);
      }
      if (!level) {
        setError(t('level.levelNotFound'));
        setLoading(false);
        return;
      }

      setLevelDataFullyLoaded(false);
      setCurrentLevel(level);

      // Phase 1: sidebar-critical data. Every fetch here (and in phase 2 below) is scoped to
      // this level server-side instead of fetching every level/topic/chapter/test/exam/lesson on
      // the whole platform and filtering client-side, so it no longer scales with the size of the
      // entire site's content.
      const cachedSidebar = getCachedLevelSidebarContent(level.id);
      const sidebarPromise = cachedSidebar ? Promise.resolve(cachedSidebar) : loadLevelSidebarContent(level.id);

      // Phase 2: everything else. Started in parallel with phase 1, not awaited yet.
      const restPromise = Promise.all([
        getChaptersByLevel(level.id),
        fetchTestsByLevel(level.id),
        fetchUserExamAttempts(),
        fetchUserTestAttempts(),
        getUserLevelProgresses(),
        getTopicContentsByLevel(level.id),
        getLessonsByLevel(level.id),
        getUserLessonVideoProgresses(),
        getUserQuizAttempts(),
      ]);

      // Commit phase 1 as soon as it resolves -- this is what makes the sidebar, lock state
      // included, paint instantly on a cache hit instead of waiting on the much heavier phase 2
      // batch below.
      const {
        topics: levelTopics, exams: examsData,
        userTopicContentProgresses: userTopicContentProgressesData, userTopicExamProgresses: userTopicExamProgressesData,
      } = await sidebarPromise;
      setTopics(levelTopics);
      setExams(examsData);
      setSidebarRows([...levelTopics, ...examsData].sort((a, b) => a.order - b.order));
      setUserTopicContentProgresses(userTopicContentProgressesData);
      setUserTopicExamProgresses(userTopicExamProgressesData);

      // Swap the loading state for the real content as soon as the sidebar-critical data is
      // ready, not the full batch -- this is the visible speed-up from the hover prefetch. The
      // slide-in itself is NOT tied to this: it runs on mount (see the effect below) so the
      // spinner enters with the container, and on a prefetch cache hit this lands before the
      // animation even starts, so the container still slides in already populated.
      setLoading(false);

      const [
        chapterData, testData, userExamAttemptsData, userTestAttemptsData,
        userLevelProgressesData, topicContentsData, levelLessonsRaw, userLessonVideoProgressesData, userQuizAttemptsData,
      ] = await restPromise;

      // Attach each lesson's pages -- fetched in a single batched request instead of one request
      // per lesson -- to compute UserLevelProgress from every video/quiz block in the level.
      const pagesByLessonId = new Map();
      const allPages = await getLessonPagesByIds(levelLessonsRaw.map(l => l.id));
      allPages.forEach(page => {
        if (!pagesByLessonId.has(page.lesson)) pagesByLessonId.set(page.lesson, []);
        pagesByLessonId.get(page.lesson).push(page);
      });
      const levelLessonsWithPages = levelLessonsRaw.map(lesson => ({
        ...lesson,
        pages: pagesByLessonId.get(lesson.id) || [],
      }));

      // Commit phase 2 together in one batch. Setting these one-by-one as each fetch resolved
      // (a previous approach) let the progress-sync effects below fire on a partially-updated
      // snapshot -- e.g. fresh topicContents/tests paired with stale userTopicContentProgresses
      // from before this refresh -- which computed prerequisite chains from incomplete data and
      // silently dropped the link between adjacent items. levelDataFullyLoaded (flipped true
      // below) additionally gates those effects until this exact commit lands, since phase 1
      // above already updated sidebarRows/topics/exams (and the progress records themselves) on
      // its own -- without that gate, the same class of bug would resurface between phase 1 and
      // phase 2 landing.
      setChapters(chapterData);
      setTests(testData);
      setUserExamAttempts(userExamAttemptsData);
      setUserTestAttempts(userTestAttemptsData);
      setUserLevelProgresses(userLevelProgressesData);
      setTopicContents(topicContentsData);
      setLevelLessons(levelLessonsWithPages);
      setUserLessonVideoProgresses(userLessonVideoProgressesData);
      setUserQuizAttempts(userQuizAttemptsData);
      setLevelDataFullyLoaded(true);
    } catch (err) {
      console.error('Error fetching level data:', err);
      setError(t('level.failedLoadLevelData'));
      setLoading(false);
    }
  };

  // Report the resolved level up to the shared Dashboard layout, which needs the numeric id to
  // request this level's history logs in the shared history modal.
  useEffect(() => {
    setActiveLevelId(currentLevel?.id ?? null);
  }, [currentLevel?.id, setActiveLevelId]);

  // Report the Save/Undo/Redo controls up to the shared Navbar (via Dashboard's outlet context)
  // -- mirrors the setActiveLevelId effect above. Cleared on unmount so leaving the level doesn't
  // leave a stale Save button wired to this instance's (now-gone) saveLevelChanges.
  useEffect(() => {
    if (!setSortSessionControls) return;
    setSortSessionControls({
      onSave: saveLevelChanges,
      onUndo: handleUndo,
      onRedo: handleRedo,
      canUndo: sessionHistoryMeta.index > 0,
      canRedo: sessionHistoryMeta.index >= 0 && sessionHistoryMeta.index < sessionHistoryMeta.length - 1,
    });
    return () => setSortSessionControls(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionHistoryMeta, setSortSessionControls]);



  // Fetch data only once on mount
  useEffect(() => {
    let isMounted = true;
    if (!userIsAuthenticated) {
      navigate('/');
      return;
    }
    if (!levelName) {
      setError(t('level.noLevelName'));
      setLoading(false);
      return;
    }

    refreshTopicsAndExams();
  }, []);

  // Slide the level view in on mount rather than when its data arrives. DashboardGrid finishes
  // its own exit animation before navigating here, so anything that delays this leaves a visible
  // blank gap between the two pages -- the loading state rides in inside the container instead.
  // The 50ms mirrors DashboardGrid's return animation and lets `anim-initial` paint first.
  useEffect(() => {
    const timeoutId = setTimeout(() => setAnimationClass('anim-enter-right'), 50);
    return () => clearTimeout(timeoutId);
  }, []);

  // Guard direct/typed/bookmarked visits to a locked level's URL -- the normal dashboard click
  // flow already hides these behind a lock icon, but that's purely a rendering choice in
  // DashboardGrid.jsx and doesn't stop someone from navigating straight to the URL. Editor mode
  // bypasses the lock, matching the level-card's own bypass. This is a client-side redirect only
  // (no backend endpoint enforces this), same as the lock icon it mirrors.
  useEffect(() => {
    if (!levelName || !currentUser?.id || userIsAdmin) return;
    let cancelled = false;
    isLevelNameLockedForUser(levelName, currentUser.id).then(locked => {
      if (!cancelled && locked) navigate('/dashboard');
    }).catch(error => {
      console.error('Error checking level access:', error);
    });
    return () => { cancelled = true; };
  }, [levelName, currentUser?.id, userIsAdmin, navigate]);

  // A 'test' topic content's completion is driven entirely by its UserTestAttempt ('chapter'
  // content is instead driven by Chapter.jsx once every lesson in it is completed).
  const isTestContentCompleted = (topicContentId) => {
    const test = tests.find(t => t.topic_content === topicContentId);
    if (!test) return false;
    const attempt = userTestAttempts.find(a => a.user === currentUser.id && a.test === test.id);
    return attempt?.completed === true;
  };

  // The title of the content (chapter or test) a locked UserTopicContentProgress is waiting on.
  const getPrerequisiteContentTitle = (prerequisiteTopicContentId) =>
    chapters.find(c => c.topic_content === prerequisiteTopicContentId)?.title
    || tests.find(test => test.topic_content === prerequisiteTopicContentId)?.title
    || t('level.previousLesson');

  // Keep UserTopicContentProgress records in sync with the current topic's contents, both
  // creating missing records and fixing stale prerequisites whenever contents are added,
  // removed, or their visibility (public/is_visible) changes. Also keeps 'test' content
  // completion synced with the user's test attempt (chapter content's completion is instead
  // owned by Chapter.jsx).
  //
  // Each content's prerequisite is the nearest PRECEDING content (by order) that is public
  // (is_visible === true); a private content in between is skipped over as a prerequisite
  // target. If no public content precedes it at all, there is no prerequisite and it starts
  // unlocked. This means completing a public chapter/test unlocks the next content even if
  // the immediately-following one is still private -- and once that private content is made
  // public again, it automatically resumes being used as the prerequisite for what follows it.
  useEffect(() => {
    // levelDataFullyLoaded: topics/sidebarRows can already be populated from phase 1 of
    // refreshTopicsAndExams while topicContents/tests (phase 2) are still their stale/empty
    // values -- without this guard this effect would compute a wrong prerequisite chain from
    // that incomplete snapshot.
    if (!levelDataFullyLoaded || !currentUser?.id || currentTopicIndex === null || !topics[currentTopicIndex]) return;
    // A topic/topicContent created this session but not yet saved has a temp id -- not a valid
    // FK for these progress-tracking endpoints. Wait until Save resolves it to a real id (this
    // effect's own dependency array already includes topics/topicContents, so it re-runs then).
    if (topics.some(t => isTempId(t.id)) || topicContents.some(tc => isTempId(tc.id))) return;
    // Guard against overlapping runs: if unrelated state changes trigger this effect again
    // while a previous run is still awaiting a create/update, that second run would start from a
    // stale local snapshot and could compute a wrong prerequisite chain -- e.g. failing to see a
    // sibling item its own run just created a moment ago, leaving the next item's
    // prerequisite_topic_content unset.
    if (topicContentProgressSyncInProgressRef.current) return;

    const orderedContents = topicContents
      .filter(tc => tc.topic === topics[currentTopicIndex].id)
      .sort((a, b) => a.order - b.order);

    if (!orderedContents.length) return;

    let cancelled = false;

    const syncProgress = async () => {
      topicContentProgressSyncInProgressRef.current = true;
      let progresses = [...userTopicContentProgresses];
      let changedAny = false;
      let lastPublicId = null;

      for (const item of orderedContents) {
        const prerequisiteId = lastPublicId;
        const existing = progresses.find(p => p.user === currentUser.id && p.topic_content === item.id);
        const prerequisiteProgress = prerequisiteId
          ? progresses.find(p => p.user === currentUser.id && p.topic_content === prerequisiteId)
          : null;
        const nextUnlocked = prerequisiteId ? !!prerequisiteProgress?.completed : true;
        // Chapter content's completion is owned by Chapter.jsx; leave whatever is already
        // stored untouched (defaults to false on creation) so we never fight its updates.
        const nextCompleted = item.type === 'test' ? isTestContentCompleted(item.id) : (existing?.completed ?? false);

        if (!existing) {
          const newProgressData = {
            user: currentUser.id,
            topic_content: item.id,
            completed: nextCompleted,
            unlocked: nextUnlocked,
            prerequisite_topic_content: prerequisiteId,
          };

          try {
            const created = await createUserTopicContentProgress(newProgressData);
            progresses = [...progresses, created];
            changedAny = true;
          } catch (error) {
            console.error('Error creating user topic content progress:', error);
          }
        } else if (
          (existing.prerequisite_topic_content ?? null) !== prerequisiteId ||
          existing.unlocked !== nextUnlocked ||
          existing.completed !== nextCompleted
        ) {
          // The prerequisite chain shifted (content added/removed, or a visibility change), the
          // prerequisite itself just got completed (unlocking this one), or a test was just
          // completed -- keep the stored prerequisite/unlocked/completed state in sync.
          try {
            const updated = await updateUserTopicContentProgress(existing.id, {
              prerequisite_topic_content: prerequisiteId,
              unlocked: nextUnlocked,
              completed: nextCompleted,
            });
            progresses = progresses.map(p => (p.id === existing.id ? updated : p));
            changedAny = true;
          } catch (error) {
            console.error('Error updating user topic content progress prerequisite:', error);
          }
        }

        if (item.is_visible) {
          lastPublicId = item.id;
        }
      }

      if (!cancelled && changedAny) {
        setUserTopicContentProgresses(progresses);
        // The cached bundle levelContentCache.js warmed for this level (if any) now holds a
        // stale snapshot of these same records -- without this, revisiting within its TTL after
        // completing something here would read back the old unlocked/completed values.
        invalidateLevelSidebarContent(currentLevel.id);
      }
      topicContentProgressSyncInProgressRef.current = false;
    };

    syncProgress();

    return () => { cancelled = true; };
  }, [levelDataFullyLoaded, currentTopicIndex, topics, topicContents, tests, userTestAttempts, currentUser?.id, userTopicContentProgresses]);

  // Keep UserTopicExamProgress records in sync with the sidebar's combined topic/exam order.
  // Every sidebar row -- topic OR exam -- gets its own progress record (topic and exam are
  // mutually exclusive on the model: exactly one is set, the other stays null). Both creating
  // missing records and fixing stale prerequisites happens here whenever sidebar items are
  // added, removed, or their visibility changes.
  //
  // Walking the sidebar in order, each row's prerequisite is the nearest preceding PUBLIC row
  // (topic or exam), skipping private rows of either type. Since prerequisite_topic can only
  // reference a Topic, it's only populated when that nearest public row is itself a topic;
  // when it's an exam, prerequisite_topic stays null but the exam's own completion is still
  // used to compute `unlocked`. A topic's own completion is derived (every visible topic
  // content under it is completed); an exam's own completion comes from the user's exam
  // attempt.
  useEffect(() => {
    // levelDataFullyLoaded: sidebarRows and userTopicExamProgresses are populated by phase 1 of
    // refreshTopicsAndExams, but topicContents/userExamAttempts are phase 2 -- without this
    // guard this effect would run against sidebarRows paired with still-empty topicContents/
    // userExamAttempts and write an incorrect "everything locked" state.
    if (!levelDataFullyLoaded || !currentUser?.id || !sidebarRows.length) return;
    // Same temp-id guard as the topic-content sync above -- a topic/exam/topicContent staged but
    // not yet saved can't be a valid FK for these progress rows yet.
    if (topics.some(t => isTempId(t.id)) || exams.some(e => isTempId(e.id)) || topicContents.some(tc => isTempId(tc.id))) return;

    const isTopicCompleted = (topicId) => {
      const visibleContents = topicContents.filter(tc => tc.topic === topicId && tc.is_visible);
      // A topic with no visible content yet has nothing to have finished -- treat it as not
      // completed rather than vacuously true, so nothing downstream unlocks prematurely.
      if (visibleContents.length === 0) return false;
      return visibleContents.every(tc => {
        const progress = userTopicContentProgresses.find(p => p.user === currentUser.id && p.topic_content === tc.id);
        return progress?.completed === true;
      });
    };

    const isExamCompleted = (examId) => {
      const attempt = userExamAttempts.find(a => a.user === currentUser.id && a.exam === examId);
      return attempt?.completed === true;
    };

    let cancelled = false;

    const syncExamProgress = async () => {
      let progresses = [...userTopicExamProgresses];
      let changedAny = false;
      // The nearest preceding public row, remembered as { type, id } (or null if none yet).
      let lastPublicRow = null;

      const orderedRows = [...sidebarRows].sort((a, b) => a.order - b.order);

      for (const item of orderedRows) {
        if (item?.id == null) continue;
        const isExamRow = Boolean(item.title && item.hasOwnProperty('duration'));
        const ownCompleted = isExamRow ? isExamCompleted(item.id) : isTopicCompleted(item.id);

        let unlocked = true;
        let prerequisiteTopicId = null;
        if (lastPublicRow) {
          prerequisiteTopicId = lastPublicRow.type === 'topic' ? lastPublicRow.id : null;
          unlocked = lastPublicRow.type === 'topic'
            ? isTopicCompleted(lastPublicRow.id)
            : isExamCompleted(lastPublicRow.id);
        }

        const existing = isExamRow
          ? progresses.find(p => p.user === currentUser.id && p.exam === item.id)
          : progresses.find(p => p.user === currentUser.id && p.topic === item.id);

        if (!existing) {
          const newProgressData = {
            user: currentUser.id,
            topic: isExamRow ? null : item.id,
            exam: isExamRow ? item.id : null,
            completed: ownCompleted,
            unlocked,
            prerequisite_topic: prerequisiteTopicId,
          };

          try {
            const created = await createUserTopicExamProgress(newProgressData);
            progresses = [...progresses, created];
            changedAny = true;
          } catch (error) {
            console.error('Error creating user topic exam progress:', formatApiError(error), error);
          }
        } else if (
          existing.completed !== ownCompleted ||
          existing.unlocked !== unlocked ||
          (existing.prerequisite_topic ?? null) !== prerequisiteTopicId
        ) {
          try {
            const updated = await updateUserTopicExamProgress(existing.id, {
              completed: ownCompleted,
              unlocked,
              prerequisite_topic: prerequisiteTopicId,
            });
            progresses = progresses.map(p => (p.id === existing.id ? updated : p));
            changedAny = true;
          } catch (error) {
            console.error('Error updating user topic exam progress:', error);
          }
        }

        if (item.is_visible) {
          lastPublicRow = { type: isExamRow ? 'exam' : 'topic', id: item.id };
        }
      }

      if (!cancelled && changedAny) {
        setUserTopicExamProgresses(progresses);
        // See the matching invalidateLevelSidebarContent call in the topic-content sync effect
        // above -- same reasoning, for this level's cached userTopicExamProgresses snapshot.
        invalidateLevelSidebarContent(currentLevel.id);
      }
    };

    syncExamProgress();

    return () => { cancelled = true; };
  }, [levelDataFullyLoaded, sidebarRows, topics, exams, topicContents, userTopicContentProgresses, userExamAttempts, userTopicExamProgresses, currentUser?.id]);

  // Keep UserLevelProgress in sync: progress_percentage is the fraction of ALL completed
  // UserLessonVideoProgress, UserQuizAttempt, UserTestAttempt, and UserExamAttempt entries
  // belonging to this level (every video block and quiz block across the level's lessons, every
  // test, and every exam) -- not just the topic/exam sidebar rows. Creates the record on first
  // visit, then patches it whenever completions change.
  useEffect(() => {
    // levelDataFullyLoaded: currentLevel is set as soon as the level is resolved (before either
    // fetch phase), and exams is phase-1 data -- without this guard, a level with exams but no
    // completed-yet tests/videos/quizzes could compute and persist an understated percentage
    // from userExamAttempts before phase 2 (which is what actually fetches it) has landed.
    if (!levelDataFullyLoaded || !currentUser?.id || !currentLevel?.id) return;
    // Same temp-id guard as the two sync effects above.
    if (topics.some(t => isTempId(t.id)) || exams.some(e => isTempId(e.id)) || topicContents.some(tc => isTempId(tc.id)) || tests.some(ts => isTempId(ts.id))) return;

    const levelTopicContentIds = new Set(
      topicContents.filter(tc => topics.some(t => t.id === tc.topic)).map(tc => tc.id)
    );
    const levelTests = tests.filter(t => levelTopicContentIds.has(t.topic_content));
    const levelExams = exams.filter(e => e.level === currentLevel.id);

    const levelBlocks = levelLessons.flatMap(lesson => (lesson.pages || []).flatMap(page => page.blocks || []));
    const levelVideoBlocks = levelBlocks.filter(b => b.block_type === 'video');
    const levelQuizBlocks = levelBlocks.filter(b => b.block_type === 'quiz' && b.quiz_block?.id);

    const videoTotal = levelVideoBlocks.length;
    const videoCompleted = levelVideoBlocks.filter(b => userLessonVideoProgresses.some(
      p => p.user === currentUser.id && p.lesson_page_block === b.id && p.video_completed === true
    )).length;

    const quizTotal = levelQuizBlocks.length;
    const quizCompleted = levelQuizBlocks.filter(b => userQuizAttempts.some(
      a => a.user === currentUser.id && a.quiz_block === b.quiz_block.id && a.completed === true
    )).length;

    const testTotal = levelTests.length;
    const testCompleted = levelTests.filter(t => userTestAttempts.some(
      a => a.user === currentUser.id && a.test === t.id && a.completed === true
    )).length;

    const examTotal = levelExams.length;
    const examCompleted = levelExams.filter(e => userExamAttempts.some(
      a => a.user === currentUser.id && a.exam === e.id && a.completed === true
    )).length;

    const total = videoTotal + quizTotal + testTotal + examTotal;
    if (total === 0) return; // nothing in this level to measure yet

    const completedCount = videoCompleted + quizCompleted + testCompleted + examCompleted;
    const nextPercentage = Math.round((completedCount / total) * 100);

    const existing = userLevelProgresses.find(p => p.user === currentUser.id && p.level === currentLevel.id);

    let cancelled = false;
    (async () => {
      try {
        if (!existing) {
          const created = await createUserLevelProgress({
            user: currentUser.id,
            level: currentLevel.id,
            progress_percentage: nextPercentage,
          });
          if (!cancelled) setUserLevelProgresses(prev => [...prev, created]);
        } else if (existing.progress_percentage !== nextPercentage) {
          const updated = await updateUserLevelProgress(existing.id, { progress_percentage: nextPercentage });
          if (!cancelled) setUserLevelProgresses(prev => prev.map(p => (p.id === updated.id ? updated : p)));
        }
      } catch (error) {
        console.error('Error syncing user level progress:', error);
      }
    })();

    return () => { cancelled = true; };
  }, [
    levelDataFullyLoaded, currentUser?.id, currentLevel?.id, topics, topicContents, tests, exams, levelLessons,
    userLessonVideoProgresses, userQuizAttempts, userTestAttempts, userExamAttempts, userLevelProgresses,
  ]);

  // Event handlers
  const handleBackToDashboard = () => {
    if (!confirmDiscardUnsavedChanges()) return;
    setAnimationClass('anim-exit-right');
    setTimeout(() => {
      navigate('/dashboard', { state: { fromLevel: true } });
    }, 300); // Match the animation duration (0.3s)
  };

  // --- Staging functions: called by the Topic/Exam/Chapter modals instead of hitting the API
  // directly. Each mirrors the shape of the API function it replaces (same args, returns a
  // Promise resolving to the created/updated object) so the modals' own code barely changes.
  // Nothing here talks to the network -- saveLevelChanges() below is the only thing that does.

  const stageCreateTopic = async (topicData) => {
    const newTopic = { ...topicData, order: sidebarRows.length, id: makeTempId() };
    setTopics(prev => [...prev, newTopic]);
    setSidebarRows(prev => [...prev, newTopic].sort((a, b) => a.order - b.order));
    pendingLogEntriesQueueRef.current.push({ title: `Created topic: ${newTopic.title}`, data: { topic_title: newTopic.title } });
    return newTopic;
  };

  const stageUpdateTopic = async (topicId, topicData) => {
    const updated = { ...topics.find(t => t.id === topicId), ...topicData, id: topicId };
    setTopics(prev => prev.map(t => (t.id === topicId ? updated : t)));
    setSidebarRows(prev => prev.map(item => (
      item.id === topicId && !(item.title && item.hasOwnProperty('duration')) ? { ...item, ...updated } : item
    )));
    pendingLogEntriesQueueRef.current.push({ title: `Updated topic: ${updated.title}`, data: { topic_title: updated.title } });
    return updated;
  };

  const stageDeleteTopic = async (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    const orphanedTopicContentIds = topicContents.filter(tc => tc.topic === topicId).map(tc => tc.id);
    setTopics(prev => prev.filter(t => t.id !== topicId));
    setSidebarRows(prev => prev.filter(item => item.id !== topicId || (item.title && item.hasOwnProperty('duration'))));
    setTopicContents(prev => prev.filter(tc => tc.topic !== topicId));
    setChapters(prev => prev.filter(c => !orphanedTopicContentIds.includes(c.topic_content)));
    setTests(prev => prev.filter(ts => !orphanedTopicContentIds.includes(ts.topic_content)));
    if (topics[currentTopicIndex]?.id === topicId) setCurrentTopicIndex(null);
    pendingLogEntriesQueueRef.current.push({ title: `Deleted topic: ${topic?.title || topicId}`, data: { topic_id: topicId } });
  };

  const stageCreateExam = async (examData) => {
    const newExam = { ...examData, order: sidebarRows.length, id: makeTempId() };
    setExams(prev => [...prev, newExam]);
    setSidebarRows(prev => [...prev, newExam].sort((a, b) => a.order - b.order));
    pendingLogEntriesQueueRef.current.push({ title: `Created exam: ${newExam.title}`, data: { exam_title: newExam.title } });
    return newExam;
  };

  const stageUpdateExam = async (examId, examData) => {
    const updated = { ...exams.find(e => e.id === examId), ...examData, id: examId };
    setExams(prev => prev.map(e => (e.id === examId ? updated : e)));
    setSidebarRows(prev => prev.map(item => (
      item.id === examId && item.title && item.hasOwnProperty('duration') ? { ...item, ...updated } : item
    )));
    pendingLogEntriesQueueRef.current.push({ title: `Updated exam: ${updated.title}`, data: { exam_title: updated.title } });
    return updated;
  };

  const stageDeleteExam = async (examId) => {
    const exam = exams.find(e => e.id === examId);
    setExams(prev => prev.filter(e => e.id !== examId));
    setSidebarRows(prev => prev.filter(item => item.id !== examId || !(item.title && item.hasOwnProperty('duration'))));
    if (exams[currentExamIndex]?.id === examId) setCurrentExamIndex(null);
    pendingLogEntriesQueueRef.current.push({ title: `Deleted exam: ${exam?.title || examId}`, data: { exam_id: examId } });
  };

  // Generic TopicContent staging -- shared by both chapter and test creation/editing (a chapter
  // or test is always backed by exactly one TopicContent row). No log entry here; the
  // chapter/test-level staging functions below own the human-readable log message.
  const stageCreateTopicContent = async (topicContentData) => {
    const siblingCount = topicContents.filter(tc => tc.topic === topicContentData.topic).length;
    const newTopicContent = { ...topicContentData, order: siblingCount, id: makeTempId() };
    setTopicContents(prev => [...prev, newTopicContent]);
    return newTopicContent;
  };

  const stageUpdateTopicContent = async (topicContentId, topicContentData) => {
    const updated = { ...topicContents.find(tc => tc.id === topicContentId), ...topicContentData, id: topicContentId };
    setTopicContents(prev => prev.map(tc => (tc.id === topicContentId ? updated : tc)));
    return updated;
  };

  const stageDeleteTopicContent = async (topicContentId) => {
    const linkedChapter = chapters.find(c => c.topic_content === topicContentId);
    const linkedTest = tests.find(ts => ts.topic_content === topicContentId);
    const label = linkedChapter?.title || linkedTest?.title || `#${topicContentId}`;
    setTopicContents(prev => prev.filter(tc => tc.id !== topicContentId));
    setChapters(prev => prev.filter(c => c.topic_content !== topicContentId));
    setTests(prev => prev.filter(ts => ts.topic_content !== topicContentId));
    pendingLogEntriesQueueRef.current.push({ title: `Deleted content: ${label}`, data: { topic_content_id: topicContentId } });
  };

  const stageCreateChapter = async (chapterData) => {
    const newChapter = { ...chapterData, id: makeTempId() };
    setChapters(prev => [...prev, newChapter]);
    pendingLogEntriesQueueRef.current.push({ title: `Created chapter: ${newChapter.title}`, data: { chapter_title: newChapter.title } });
    return newChapter;
  };

  const stageUpdateChapter = async (chapterId, chapterData) => {
    const updated = { ...chapters.find(c => c.id === chapterId), ...chapterData, id: chapterId };
    setChapters(prev => prev.map(c => (c.id === chapterId ? updated : c)));
    pendingLogEntriesQueueRef.current.push({ title: `Updated chapter: ${updated.title}`, data: { chapter_title: updated.title } });
    return updated;
  };

  const stageCreateTest = async (testData) => {
    const newTest = { ...testData, id: makeTempId() };
    setTests(prev => [...prev, newTest]);
    pendingLogEntriesQueueRef.current.push({ title: `Created test: ${newTest.title}`, data: { test_title: newTest.title } });
    return newTest;
  };

  const stageUpdateTest = async (testId, testData) => {
    const updated = { ...tests.find(ts => ts.id === testId), ...testData, id: testId };
    setTests(prev => prev.map(ts => (ts.id === testId ? updated : ts)));
    pendingLogEntriesQueueRef.current.push({ title: `Updated test: ${updated.title}`, data: { test_title: updated.title } });
    return updated;
  };

  // --- Admin access staging: called by the Topic/Exam/Chapter modals' admin-access UI instead of
  // hitting the API directly. Each just records the modal's selection against the item's current
  // (temp or real) id -- saveLevelChanges() below is what actually diffs it against the database
  // and creates/deletes AdminPermission rows, exactly like every other staged edit here.
  const stageSetTopicAdminAccess = (topicId, userIds) => {
    setTopicAdminAccess(prev => ({ ...prev, [topicId]: userIds }));
    pendingLogEntriesQueueRef.current.push({ title: 'Updated topic admin access', data: { topic_id: topicId } });
  };

  const stageSetExamAdminAccess = (examId, userIds) => {
    setExamAdminAccess(prev => ({ ...prev, [examId]: userIds }));
    pendingLogEntriesQueueRef.current.push({ title: 'Updated exam admin access', data: { exam_id: examId } });
  };

  const stageSetChapterAdminAccess = (chapterId, userIds) => {
    setChapterAdminAccess(prev => ({ ...prev, [chapterId]: userIds }));
    pendingLogEntriesQueueRef.current.push({ title: 'Updated chapter admin access', data: { chapter_id: chapterId } });
  };

  const stageSetTestAdminAccess = (testId, userIds) => {
    setTestAdminAccess(prev => ({ ...prev, [testId]: userIds }));
    pendingLogEntriesQueueRef.current.push({ title: 'Updated test admin access', data: { test_id: testId } });
  };

  // --- Save: diffs the current staged snapshot against the last-saved baseline and creates/
  // updates/deletes as needed. Order respects FK dependencies (topics/exams before the content
  // that references them) and deletes-before-creates within each entity type (Topic.title is
  // globally unique, so deleting "Foo" and creating a new "Foo" in the same session must delete
  // first or the create hits a collision even though the final state is valid). Each successful
  // create/update commits its real id into React state immediately (not batched at the end), so
  // a mid-save failure leaves already-succeeded items safely resolved -- retrying treats them as
  // updates instead of re-creating duplicates. No direct chapter/test delete calls -- both
  // cascade server-side from their TopicContent's delete, matching how the modals already work.
  const saveLevelChanges = async () => {
    if (!userIsStaff || !editorMode) return;
    if (!currentLevel?.id || isSavingLevel || !hasUnsavedChanges()) return;
    setIsSavingLevel(true);

    const baseline = sessionBaselineRef.current;
    const currentSnapshot = sessionHistoryRef.current[sessionHistoryIndexRef.current];

    const safeDelete = async (fn, id) => {
      try { await fn(id); } catch (error) { console.error('Error deleting during save:', error); }
    };

    // Persists a staged admin-access selection for one item, once its id is resolved to a real
    // one -- undefined userIds means the admin never opened/touched that item's admin access UI
    // this session, so existing database permissions are left untouched rather than wiped out.
    const syncStagedAdminAccess = async (userIds, realId, getPermissions, createPermission, deletePermission, foreignKey) => {
      if (userIds === undefined || !realId) return;
      try {
        const existingPermissions = await getPermissions();
        const permissionsForItem = Array.isArray(existingPermissions)
          ? existingPermissions.filter((permission) => Number(permission[foreignKey]?.id ?? permission[foreignKey]) === Number(realId))
          : [];
        for (const permission of permissionsForItem) {
          await deletePermission(permission.id);
        }
        for (const userId of userIds) {
          await createPermission({ [foreignKey]: realId, user: userId });
        }
      } catch (error) {
        console.error('Error syncing admin access during save:', error);
      }
    };

    try {
      // --- Topics: delete, then create/update ---
      const currentTopicIds = new Set(currentSnapshot.topics.map(t => t.id));
      const deletedTopicIds = new Set();
      for (const baseTopic of baseline.topics) {
        if (!currentTopicIds.has(baseTopic.id)) {
          deletedTopicIds.add(baseTopic.id);
          await safeDelete(deleteTopic, baseTopic.id);
        }
      }
      const topicIdMap = {};
      const resolvedTopics = [];
      for (const topic of currentSnapshot.topics) {
        let realTopicId;
        if (isTempId(topic.id)) {
          const { id: tempId, ...topicData } = topic;
          const created = await createTopic(topicData);
          topicIdMap[tempId] = created.id;
          resolvedTopics.push(created);
          setTopics(prev => prev.map(t => (t.id === tempId ? created : t)));
          realTopicId = created.id;
        } else {
          const { id, ...topicData } = topic;
          const updated = await updateTopic(id, topicData);
          resolvedTopics.push(updated);
          setTopics(prev => prev.map(t => (t.id === id ? updated : t)));
          realTopicId = id;
        }
        await syncStagedAdminAccess(
          currentSnapshot.topicAdminAccess?.[topic.id], realTopicId,
          getTopicAdminPermissions, createTopicAdminPermission, deleteTopicAdminPermission, 'topic'
        );
      }

      // --- Exams: delete, then create/update (no dependents) ---
      const currentExamIds = new Set(currentSnapshot.exams.map(e => e.id));
      for (const baseExam of baseline.exams) {
        if (!currentExamIds.has(baseExam.id)) await safeDelete(deleteExam, baseExam.id);
      }
      const resolvedExams = [];
      for (const exam of currentSnapshot.exams) {
        let realExamId;
        if (isTempId(exam.id)) {
          const { id: tempId, ...examData } = exam;
          const created = await createExam(examData);
          resolvedExams.push(created);
          setExams(prev => prev.map(e => (e.id === tempId ? created : e)));
          realExamId = created.id;
        } else {
          const updated = await updateExam(exam.id, exam);
          resolvedExams.push(updated);
          setExams(prev => prev.map(e => (e.id === exam.id ? updated : e)));
          realExamId = exam.id;
        }
        await syncStagedAdminAccess(
          currentSnapshot.examAdminAccess?.[exam.id], realExamId,
          getExamAdminPermissions, createExamAdminPermission, deleteExamAdminPermission, 'exam'
        );
      }
      setSidebarRows([...resolvedTopics, ...resolvedExams].sort((a, b) => a.order - b.order));

      // --- Topic contents: delete (skip ones whose topic was already deleted -- cascade-covered
      // by the topic delete above), then create/update, resolving `.topic` through the topic map.
      const currentTopicContentIds = new Set(currentSnapshot.topicContents.map(tc => tc.id));
      for (const baseTC of baseline.topicContents) {
        if (!currentTopicContentIds.has(baseTC.id) && !deletedTopicIds.has(baseTC.topic)) {
          await safeDelete(deleteTopicContent, baseTC.id);
        }
      }
      const topicContentIdMap = {};
      const resolvedTopicContents = [];
      for (const tc of currentSnapshot.topicContents) {
        const resolvedTopic = isTempId(tc.topic) ? (topicIdMap[tc.topic] ?? tc.topic) : tc.topic;
        if (isTempId(tc.id)) {
          const { id: tempId, ...tcData } = tc;
          const created = await createTopicContent({ ...tcData, topic: resolvedTopic });
          topicContentIdMap[tempId] = created.id;
          resolvedTopicContents.push(created);
          setTopicContents(prev => prev.map(item => (item.id === tempId ? created : item)));
        } else {
          const { id, ...tcData } = tc;
          const updated = await updateTopicContent(id, { ...tcData, topic: resolvedTopic });
          resolvedTopicContents.push(updated);
          setTopicContents(prev => prev.map(item => (item.id === id ? updated : item)));
        }
      }

      // --- Chapters: create/update, resolving `.topic_content` through the topicContent map ---
      const resolvedChapters = [];
      for (const chapter of currentSnapshot.chapters) {
        const resolvedTopicContentId = isTempId(chapter.topic_content)
          ? (topicContentIdMap[chapter.topic_content] ?? chapter.topic_content)
          : chapter.topic_content;
        let realChapterId;
        if (isTempId(chapter.id)) {
          const { id: tempId, ...chapterData } = chapter;
          const created = await createChapter({ ...chapterData, topic_content: resolvedTopicContentId });
          resolvedChapters.push(created);
          setChapters(prev => prev.map(c => (c.id === tempId ? created : c)));
          realChapterId = created.id;
        } else {
          const { id, ...chapterData } = chapter;
          const updated = await updateChapter(id, { ...chapterData, topic_content: resolvedTopicContentId });
          resolvedChapters.push(updated);
          setChapters(prev => prev.map(c => (c.id === id ? updated : c)));
          realChapterId = id;
        }
        await syncStagedAdminAccess(
          currentSnapshot.chapterAdminAccess?.[chapter.id], realChapterId,
          getChapterAdminPermissions, createChapterAdminPermission, deleteChapterAdminPermission, 'chapter'
        );
      }

      // --- Tests: same pattern as chapters ---
      const resolvedTests = [];
      for (const test of currentSnapshot.tests) {
        const resolvedTopicContentId = isTempId(test.topic_content)
          ? (topicContentIdMap[test.topic_content] ?? test.topic_content)
          : test.topic_content;
        let realTestId;
        if (isTempId(test.id)) {
          const { id: tempId, ...testData } = test;
          const created = await createTest({ ...testData, topic_content: resolvedTopicContentId });
          resolvedTests.push(created);
          setTests(prev => prev.map(ts => (ts.id === tempId ? created : ts)));
          realTestId = created.id;
        } else {
          const { id, ...testData } = test;
          const updated = await updateTest(id, { ...testData, topic_content: resolvedTopicContentId });
          resolvedTests.push(updated);
          setTests(prev => prev.map(ts => (ts.id === id ? updated : ts)));
          realTestId = id;
        }
        await syncStagedAdminAccess(
          currentSnapshot.testAdminAccess?.[test.id], realTestId,
          getTestAdminPermissions, createTestAdminPermission, deleteTestAdminPermission, 'test'
        );
      }

      // Flush the queued log entries as history log rows, dated at save time.
      const logEntries = currentSnapshot.logEntries || [];
      await Promise.all(logEntries.map(entry => createLevelHistoryLog({
        level: currentLevel.id,
        user_id: currentUser.id,
        log_title: entry.title,
        log_date: new Date().toISOString(),
        log_data: entry.data || {},
      }).catch(error => console.error('Error creating level history log:', error))));

      invalidateLevelSidebarContent(currentLevel.id);
      // Every staged admin-access selection was just persisted (or intentionally skipped -- see
      // syncStagedAdminAccess) above, so the maps themselves -- keyed by ids that may no longer
      // even exist after temp-id resolution -- have nothing left to stage; clear them along with
      // the rest of the now-saved baseline.
      setTopicAdminAccess({});
      setExamAdminAccess({});
      setChapterAdminAccess({});
      setTestAdminAccess({});
      resetSessionHistory({
        topics: resolvedTopics, exams: resolvedExams, topicContents: resolvedTopicContents,
        chapters: resolvedChapters, tests: resolvedTests,
        topicAdminAccess: {}, examAdminAccess: {}, chapterAdminAccess: {}, testAdminAccess: {},
      });
      showToast(t('level.sortChangesSaved'));
      return { resolvedTopics, resolvedExams, resolvedTopicContents, resolvedChapters, resolvedTests };
    } catch (error) {
      // The message matters more here than anywhere else on the page -- a save aborts on the
      // first rejected request, so whatever the server objected to is the only thing standing
      // between the admin and their unsaved session.
      const reason = formatApiError(error);
      console.error('Error saving level changes:', reason, error);
      showToast(reason ? `${t('level.sortSaveFailed')} (${reason})` : t('level.sortSaveFailed'), 'error');
      return null;
    } finally {
      setIsSavingLevel(false);
    }
  };

  // Modal handlers -- staging already updates state directly, so closing a modal no longer
  // refetches anything (a refetch here would overwrite staged-but-unsaved edits with stale
  // server data).
  const openTopicModal = (action, topic=null) => {
    setModalTopicAction(action);
    setSelectedTopic(topic);
  };

  const closeTopicModal = () => {
    setModalTopicAction(null);
    setSelectedTopic(null);
  };

  const openExamModal = (action, examType, exam = null, test = null, topic = null, examAttempt = null) => {
    setModalExamAction(action);
    setSelectedExamType(examType);
    setSelectedExam(exam);
    setSelectedExamAttempt(examAttempt);
    setSelectedTestAttempt(examAttempt);
    setSelectedTest(test)
    // For tests, always use the current topic if not provided
    if (examType === 'test') {
      setSelectedTopic(topic || getCurrentTopic());
    } else {
      setSelectedTopic(topic);
    }
  };
  const closeExamModal = () => {
    setModalExamAction(null);
    setSelectedExam(null);
    setSelectedExamAttempt(null);
    setSelectedTestAttempt(null);
    setSelectedTest(null);
    setSelectedExamType(null);
  }

  const openChapterModal = (action, chapter = null) => {
    setModalChapterAction(action);
    setSelectedChapter(chapter);
  };

  const closeChapterModal = () => {
    setModalChapterAction(null);
    setSelectedChapter(null);
  };


  const getCurrentTopic = () => {
    if (currentTopicIndex !== null && topics[currentTopicIndex]) {
      return topics[currentTopicIndex];
    }
    return null;
  };

  const getCurrentExamAttempt = (examId) => {
    return userExamAttempts.find(attempt => attempt.user === currentUser?.id && attempt.exam === examId) || null;
  };

  const getCurrentTestAttempt = (testId) => {
    return userTestAttempts.find(attempt => attempt.user === currentUser?.id && attempt.test === testId) || null;
  };

  // Memoized so this array keeps a stable reference across re-renders that don't actually change
  // the current topic's contents (e.g. the progress-sync effects updating unrelated state while
  // the user is mid-drag). ReactSortable re-syncs its DOM to the `list` prop on every reference
  // change, and doing that while a native drag is in progress is what caused the sort to visually
  // snap back / silently drop the reorder.
  const currentTopicId = currentTopicIndex !== null ? topics[currentTopicIndex]?.id : null;
  const currentTopicContents = useMemo(
    () => (currentTopicId == null ? [] : topicContents.filter(tc => tc.topic === currentTopicId)),
    [topicContents, currentTopicId]
  );
  // ReactSortable's `list` and rendered `children` must correspond 1:1 by index -- a
  // topicContent row with no matching Test/Chapter record can't render a child at all,
  // so it's excluded from both here rather than returning null from within .map() below
  // (react-sortablejs crashes reading `.props` off a null child).
  const sortableTopicContents = useMemo(
    () => currentTopicContents.filter((item) => (
      item.type === 'test'
        ? tests.some(t => t.topic_content === item.id)
        : chapters.some(c => c.topic_content === item.id)
    )),
    [currentTopicContents, tests, chapters]
  );

  // Unified reorder handler for topic content (chapters and tests) -- purely local, like the
  // sidebar reorder above; persisted only by saveLevelChanges().
  const handleTopicContentReorder = (newOrder) => {
    const topicId = getCurrentTopic()?.id;
    if (!topicId) return;
    const orderedItems = newOrder.filter(Boolean).map((item, index) => ({ ...item, order: index }));
    // react-sortablejs calls setList once on every mount of the sortable, not just on real
    // drags -- its constructor maps the initial list through Object.assign(item, {chosen:
    // false, selected: false}) and immediately invokes setList with the result. The topic-
    // content sortable unmounts/remounts whenever the container swaps between the "no material
    // yet" placeholder and the list (i.e. switching to/from an empty topic), so simply clicking
    // a topic could trigger this and get treated as a real edit. Bail out unless the order (by
    // id) actually changed.
    const previousOrderIds = sortableTopicContents.map(item => item.id).join(',');
    const newOrderIds = orderedItems.map(item => item.id).join(',');
    if (previousOrderIds === newOrderIds) return;
    // Only the current topic's rows pass through `newOrder` (it's the sortable subset) --
    // keep every other topic's rows untouched instead of replacing the whole array.
    setTopicContents(prev => [
      ...prev.filter(tc => tc.topic !== topicId),
      ...orderedItems,
    ]);
    pendingLogEntriesQueueRef.current.push({ title: `Reordered ${getCurrentTopic()?.title || 'topic'}'s contents`, data: { topic_id: topicId } });
  };

  const updateChapterSizeLive = (chapIdx) => {
    const topicContent = topicContents.find(tc => tc.id === chapIdx); 
    const fontSize = topicContent.font_size;
    if (topicContent) {
      if (!fontSize || fontSize === '') {
          document.getElementById(`chapTitle-${chapIdx}`).style.fontSize = 20 + 'px';
      } else {
          document.getElementById(`chapTitle-${chapIdx}`).style.fontSize = fontSize + 'px';
      }
    } else {
      console.error(`Topic content with id ${chapIdx} not found for live size update.`);
    }
  }

  const adjustLiveSize = (idx, delta) => {
    const topicContent = topicContents.find(tc => tc.id === idx);
    if (topicContent) {
        const fontSize = topicContent.font_size;

        if (fontSize) {
          const newSize = fontSize + delta;

          let finalStr = newSize.toString();

          const inputEl = document.getElementById('sizeInput-' + idx);
          if(inputEl) inputEl.value = finalStr;

          // Local-only, like every other edit -- flows through the same auto-capture/undo/Save
          // pipeline (Save's generic topicContent update pass picks up font_size automatically).
          setTopicContents(prev => prev.map(tc => (tc.id === idx ? { ...tc, font_size: newSize } : tc)));
          updateChapterSizeLive(idx);
        }

    } else {
      console.error(`Topic content with id ${idx} not found.`);
    }

  }

  return (
    <>
      {/* Topic Modal */}
      {modalTopicAction && (
        <div className="modal-overlay show" onClick={closeTopicModal}>
          <div onClick={(e) => e.stopPropagation()}>
            <ModalTopic
              Action={modalTopicAction}
              selectedLevel={currentLevel}
              selectedTopic={selectedTopic}
              closeModal={closeTopicModal}
              stageCreateTopic={stageCreateTopic}
              stageUpdateTopic={stageUpdateTopic}
              stageDeleteTopic={stageDeleteTopic}
              topicAdminAccess={topicAdminAccess}
              stageSetTopicAdminAccess={stageSetTopicAdminAccess}
            />
          </div>
        </div>
      )}
      {/* Exam Modal */}
      {modalExamAction && selectedExamType && ( (selectedTopic) || currentLevel ) && (
        <div className="modal-overlay show" onClick={closeExamModal}>
          <div onClick={(e) => e.stopPropagation()}>
            <ModalExam
              Action={modalExamAction}
              selectedTopic={selectedTopic}
              selectedLevel={currentLevel}
              selectedExam={selectedExam}
              selectedExamAttempt={selectedExamAttempt}
              selectedTestAttempt={selectedTestAttempt}
              selectedTest={selectedTest}
              examType={selectedExamType}
              closeModal={closeExamModal}
              tests={tests}
              stageCreateExam={stageCreateExam}
              stageUpdateExam={stageUpdateExam}
              stageDeleteExam={stageDeleteExam}
              stageCreateTopicContent={stageCreateTopicContent}
              stageUpdateTopicContent={stageUpdateTopicContent}
              stageDeleteTopicContent={stageDeleteTopicContent}
              stageCreateTest={stageCreateTest}
              stageUpdateTest={stageUpdateTest}
              confirmDiscardUnsavedChanges={confirmDiscardUnsavedChanges}
              examAdminAccess={examAdminAccess}
              stageSetExamAdminAccess={stageSetExamAdminAccess}
              testAdminAccess={testAdminAccess}
              stageSetTestAdminAccess={stageSetTestAdminAccess}
            />
          </div>
        </div>
      )}
      {/* Chapter Modal */}
      {modalChapterAction && (
        <div className="modal-overlay show" onClick={closeChapterModal}>
          <div onClick={(e) => e.stopPropagation()}>
            <ModalChapter
              Action={modalChapterAction}
              selectedTopic={getCurrentTopic()}
              selectedChapter={selectedChapter}
              closeModal={closeChapterModal}
              chapters={chapters}
              stageCreateTopicContent={stageCreateTopicContent}
              stageUpdateTopicContent={stageUpdateTopicContent}
              stageDeleteTopicContent={stageDeleteTopicContent}
              stageCreateChapter={stageCreateChapter}
              stageUpdateChapter={stageUpdateChapter}
              chapterAdminAccess={chapterAdminAccess}
              stageSetChapterAdminAccess={stageSetChapterAdminAccess}
            />
          </div>
        </div>
      )}

      <div className="Level">
        {error && <p>{t('level.errorPrefix', { error })}</p>}
        {/* The loading state sits INSIDE the animated container rather than beside it, so the
            dashboard -> level hand-off is one continuous slide-in: the spinner enters with the
            container and is swapped for the real content in place once the data lands. Gated on
            !error rather than currentLevel because that object is itself a fetch on a direct URL
            visit, and an empty screen until it resolves is the same break in the transition. */}
        {!error && (
          <div id="topicView" style={{paddingTop: '100px'}}>
            <div className={`smooth-container ${animationClass}`} id="topicTransitionContainer">
              {loading || !currentLevel ? (
                <Loading message={t('level.loadingLevel')} />
              ) : (
                <>
                <div className="level-header">
                    <h1 className="activeLevelTitle" style={{margin: 0}}>{currentLevel.name}</h1>
                    {(userIsAdmin || canEditSelectedExam() || canEditSelectedTopicContent()) && editorMode && sessionHistoryMeta.length > 0 && (
                      <span className={`save-status-badge${hasUnsavedChanges() ? ' save-status-badge--dirty' : ''}`}>
                        {hasUnsavedChanges() ? t('level.unsavedChanges') : t('level.upToDate')}
                      </span>
                    )}
                    <button className="btn-primary btn-back" onClick={handleBackToDashboard}>{t('level.backToDashboard')}</button>
                </div>

                <div className="app-container">
                  <aside className="topic-sidebar">
                    <p style={{fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '20px', padding: '0 18px'}}>{t('level.topicsLabel')}</p>

                    <div id="sidebarMenu" className='sidebarMenu'>
                      {(sidebarRows.length === 0 && userIsStaff) || (sidebarRows.filter(item => item.is_visible).length === 0 && !userIsStaff) ? (
                        <div style={{color: '#ccc', padding: '0 18px', fontSize: '0.9rem'}}>{t('level.noTopicsOrExams')}</div>
                      ) : userIsAdmin && editorMode ? (
                        <ReactSortable
                          list={sidebarRows}
                          setList={newList => {
                            // react-sortablejs calls setList once on every mount of the
                            // sortable, not just on real drags (see the matching comment in
                            // handleTopicContentReorder) -- bail out unless the order (by id)
                            // actually changed.
                            const previousOrderIds = sidebarRows.map(item => item.id).join(',');
                            const newOrderIds = newList.map(item => item.id).join(',');
                            if (previousOrderIds === newOrderIds) return;
                            const orderedList = newList.map((item, idx) => ({ ...item, order: idx }));
                            // Topic and Exam are separate tables with independent auto-increment
                            // PKs, so an id lookup here (`exams.some(e => e.id === item.id)`) could
                            // match the wrong row whenever a topic and an exam happen to share an
                            // id -- routing that row's order update to the wrong endpoint and
                            // silently losing the real one. Discriminate by shape instead, like
                            // everywhere else in this file (Topic has no `duration` field).
                            const newTopics = orderedList.filter(item => !(item.title && item.hasOwnProperty('duration')));
                            const newExams = orderedList.filter(item => item.title && item.hasOwnProperty('duration'));
                            // Update sidebarRows AND the separate topics/exams arrays (read by the
                            // row click handlers and the progress-sync effects) so they all agree
                            // with the drop position immediately.
                            setSidebarRows(orderedList);
                            setTopics(newTopics);
                            setExams(newExams);
                            // Purely local -- like every other edit here, persisted only by
                            // saveLevelChanges() when the user clicks Save.
                            pendingLogEntriesQueueRef.current.push({ title: 'Reordered topics/exams', data: {} });
                          }}
                          animation={150}
                          handle=".topic-handle, .finalexam-handle"
                          ghostClass="sortable-ghost"
                          disabled={!userIsAdmin || !editorMode}
                          scroll={true}
                          scrollSensitivity={60}
                                 scrollSpeed={25}
                          bubbleScroll={true}
                          forceFallback={true}
                          fallbackOnBody={true}
                          fallbackTolerance={4}
                          onEnd={suppressClickAfterDrag}
                        >
                          {sidebarRows.map((item, index) => {
                            if (item.title && item.hasOwnProperty('duration')) {
                              // Exam
                              return (
                                <div key={`finalexam-${item.id}`} className="sidebar-row">
                                  <button
                                    className={`sidebar-item exam-item${currentExamIndex === exams.findIndex(e => e.id === item.id) ? ' active' : ''}`}
                                    style={
                                      currentExamIndex === exams.findIndex(e => e.id === item.id)
                                        ? { color: 'white', background: 'var(--exam-color)' }
                                        : {color: 'var(--exam-color)', background: '#fdf2f8'}
                                    }
                                    onClick={() => {
                                      if (suppressClickAfterDragRef.current) return;
                                      setCurrentTopicIndex(null);
                                      setCurrentExamIndex(exams.findIndex(e => e.id === item.id));
                                      setSelectedTopic(null)
                                      setSelectedExam(item)
                                    }}
                                  >
                                  <span className="topic-handle" style={{display: 'inline-block'}}>⋮⋮</span>
                                  <span style={{flex: 1, textAlign: textAlignStart}}>{item.title}</span>
                                  </button>
                                  <div className="sidebar-actions" style={{display: 'flex'}}>
                                    <button className="mini-btn btn-edit" onClick={() => openExamModal('Update', 'exam', item)}>
                                      ✎
                                    </button>
                                    <button className="mini-btn btn-del" onClick={() => openExamModal('Delete', 'exam', item)}>
                                      ×
                                    </button>
                                  </div>
                                </div>
                              );
                            } else {
                              // Topic
                              return (
                                <div key={`topic-${item.id}`} className="sidebar-row">
                                  <button
                                    className={`sidebar-item ${getCurrentTopic()?.id === item.id ? 'active' : ''}`}
                                    onClick={() => {
                                      if (suppressClickAfterDragRef.current) return;
                                      setCurrentExamIndex(null);
                                      setCurrentTopicIndex(topics.findIndex(t => t.id === item.id));
                                      setSelectedTopic(item)
                                      setSelectedExam(null)
                                    }}
                                  >
                                    <span className="topic-handle" style={{display: 'inline-block'}}>⋮⋮</span>
                                    <span style={{flex: 1, textAlign: textAlignStart}}>{item.title}</span>
                                  </button>
                                  <div className="sidebar-actions" style={{display: 'flex'}}>
                                    <button className="mini-btn btn-edit" onClick={() => openTopicModal('Update', item)}>✎</button>
                                    <button className="mini-btn btn-del" onClick={() => openTopicModal('Delete', item)}>×</button>
                                  </div>
                                </div>
                              );
                            }
                          })}
                        </ReactSortable>
                      ) : (
                        sidebarRows.map((item, index) => {
                          const isExamRow = Boolean(item.title && item.hasOwnProperty('duration'));
                          const userTopicExamProgress = userTopicExamProgresses.find(progress => (
                            progress.user === currentUser?.id && (isExamRow ? progress.exam === item.id : progress.topic === item.id)
                          ));
                          const isLocked = userTopicExamProgress && !userTopicExamProgress.unlocked && !(userIsStaff || userIsAdmin);
                          if (isExamRow) {
                            // Exam
                            return (
                              (item.is_visible || userIsStaff) && (
                              <div key={`finalexam-${item.id}`} className="sidebar-row">
                                <button
                                  className={`sidebar-item exam-item${currentExamIndex === exams.findIndex(e => e.id === item.id) ? ' active' : ''} ${isLocked ? 'locked-card' : ''}`}
                                    style={
                                      currentExamIndex === exams.findIndex(e => e.id === item.id)
                                        ? { color: 'white', background: 'var(--exam-color)' }
                                        : {color: 'var(--exam-color)', background: '#fdf2f8'}
                                    }                                  
                                    onClick={() => { if (!isLocked) {
                                      setCurrentTopicIndex(null);
                                      setCurrentExamIndex(exams.findIndex(t => t.id === item.id));
                                      setSelectedTopic(null)
                                      setSelectedExam(item)
                                    }
                                  }}
                                >
                                  <span style={{flex: 1, textAlign: textAlignStart}}>{item.title}</span>
                                  {
                                    isLocked && <span style={{marginLeft: '10px', fontSize: '0.8rem', color: 'var(--text-muted)'}}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="25"
                                        height="25"
                                        viewBox="0 0 24 20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-label="Locked module"
                                        role="img"
                                      >
                                        <title>Locked module</title>
                                        <rect x="3" y="10" width="18" height="11" rx="2.5" fill="currentColor" fillOpacity="0.12" />
                                        <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                                        <circle cx="12" cy="15" r="1.25" fill="currentColor" stroke="none" />
                                        <path d="M12 16.25V18" />
                                        <path d="M5.5 10h13" opacity="0.45" />
                                      </svg> {/* Detailed lock icon */}
                                    </span>
                                  }
                                </button>
                              </div>
                            ));
                          } else {
                            // Topic
                            return (
                              (item.is_visible || userIsStaff) && (
                              <div key={`topic-${item.id}`} className="sidebar-row">
                                <button
                                  className={`sidebar-item ${getCurrentTopic()?.id === item.id ? 'active' : ''} ${isLocked ? 'locked-card' : ''}`}
                                  onClick={() => { if (!isLocked) {
                                    setCurrentExamIndex(null);
                                    setCurrentTopicIndex(topics.findIndex(t => t.id === item.id));
                                    setSelectedTopic(item)
                                    setSelectedExam(null)
                                    }
                                  }}
                                >
                                  <span style={{flex: 1, textAlign: textAlignStart}}>{item.title}</span>
                                  {
                                    isLocked && <span style={{marginLeft: '10px', fontSize: '0.8rem', color: 'var(--text-muted)'}}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="25"
                                        height="25"
                                        viewBox="0 0 24 20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-label="Locked module"
                                        role="img"
                                      >
                                        <title>Locked module</title>
                                        <rect x="3" y="10" width="18" height="11" rx="2.5" fill="currentColor" fillOpacity="0.12" />
                                        <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                                        <circle cx="12" cy="15" r="1.25" fill="currentColor" stroke="none" />
                                        <path d="M12 16.25V18" />
                                        <path d="M5.5 10h13" opacity="0.45" />
                                      </svg> {/* Detailed lock icon */}
                                    </span>
                                  }
                                </button>
                              </div>
                            ));
                          }
                        })
                      )}
                    </div>
                    {userIsAdmin && editorMode && (
                      <>
                      <button 
                        id="btnAdminAddTopic"
                        className="admin-btn-trigger" 
                        style={{marginTop: '20px', width: '100%', justifyContent: 'center'}}
                        onClick={() => openTopicModal('Add')}
                      >
                        {t('level.addTopic')}
                      </button>
                      <button id="btnAdminAddExam" className="admin-btn-trigger"
                        style={{marginTop: '10px', width: '100%', justifyContent: 'center', background: 'var(--exam-color)', boxShadow: '0 4px 6px rgba(124, 58, 237, 0.2)'}}
                        onClick={() => openExamModal('Add', 'exam')}
                      >
                          {t('level.addExam')}
                      </button>
                      </>
                    )}
                  </aside>

                  <main className="topic-content">
                    <div id="canvasContent">
                      {/* --- Exam Hero Card --- */}
                      {!currentTopicIndex && currentExamIndex !== null && exams[currentExamIndex] ? (
                        <div className="exam-hero-card">
                          {(() => {
                            const currentExam = exams[currentExamIndex];
                            const currentAttempt = getCurrentExamAttempt(currentExam.id);
                            const canReviewAnswers = currentAttempt?.completed === true && currentAttempt?.is_solving === false;
                            const startButtonText = currentAttempt
                              ? (currentAttempt.completed ? t('level.startExam') : t('level.resumeExam'))
                              : t('level.startExam');

                            return (
                              <>
                          <div className="exam-hero-title">{exams[currentExamIndex]?.title || t('level.examFallbackTitle')}{currentAttempt && currentAttempt.completed && (currentAttempt.passed ? t('level.passedSuffix') : t('level.failedSuffix'))}</div>
                          <div className="exam-hero-desc">
                            {exams[currentExamIndex]?.description || (
                              <>{t('level.examIntroPrefix')} <b>{currentLevel.name || t('level.thisLevelFallback')}</b> {t('level.examIntroSuffix')}</>
                            )}
                          </div>
                          <div className="exam-stats-large">
                            <div className="exam-stat-group">
                              <div className="es-value">{exams[currentExamIndex]?.questions_count || 20}</div>
                              <div className="es-label">{t('level.questions')}</div>
                            </div>
                            <div className="exam-stat-group">
                              <div className="es-value">{exams[currentExamIndex]?.duration ? t('level.minutesShort', { count: exams[currentExamIndex].duration }) : t('level.minutesShort', { count: 30 })}</div>
                              <div className="es-label">{t('level.duration')}</div>
                            </div>
                            <div className="exam-stat-group">
                              <div className="es-value">{currentAttempt && currentAttempt.completed && !currentAttempt.is_solving ? `${currentAttempt.score}/${exams[currentExamIndex].max_score}` : t('level.notSolved')}</div>
                              <div className="es-label">{t('level.score')}</div>
                            </div>
                          </div>
                          {(!currentAttempt || !currentAttempt.completed || currentAttempt.is_solving) && (
                            <button className="btn-exam-start" onClick={() => {
                              openExamModal('Start', 'exam', exams[currentExamIndex], null, null, currentAttempt);
                            }}>
                              {t('level.startExam')}
                            </button>
                          )}
                          {canReviewAnswers && (
                            <button
                              className="btn-exam-start"
                              style={{ marginLeft: '10px', background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                              onClick={() => {
                                if (!confirmDiscardUnsavedChanges()) return;
                                navigate(`/dashboard/${levelName}/exams/${exams[currentExamIndex].title}`, {
                                  state: { selectedExam: exams[currentExamIndex], isReviewing: true },
                                });
                              }}
                            >
                              {t('level.reviewAnswers')}
                            </button>
                          )}
                          { (userIsAdmin || canEditSelectedExam()) && editorMode && (
                          <div className="admin-exam-actions" style={{marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'center'}}>
                            <button className="admin-btn-trigger" style={{background: 'var(--edit-color)'}} onClick={() => openExamModal('Update', 'exam', exams[currentExamIndex])}>{t('level.editSettings')}</button>
                            <button className="admin-btn-trigger" style={{background: 'var(--accent)'}}
                              onClick={async () => {
                                if (!confirmDiscardUnsavedChanges()) return;
                                const examIndex = currentExamIndex;
                                let examToEdit = exams[examIndex];
                                // A freshly-created exam only exists locally (temp id) until Save runs --
                                // jumping straight into the question editor against a temp id means the
                                // backend has no exam to attach the seeded placeholder question to, so it
                                // silently fails to appear until the level is saved and reopened. Saving
                                // here first resolves it to a real id so the placeholder seeds correctly.
                                if (isTempId(examToEdit.id)) {
                                  const result = await saveLevelChanges();
                                  if (!result) return;
                                  examToEdit = result.resolvedExams[examIndex];
                                  if (!examToEdit) return;
                                }
                                navigate(`/dashboard/${levelName}/exams/${examToEdit.title}`, { state: { selectedExam: examToEdit, isOnlyEditing: true } });
                              }}
                            >
                              {t('level.editQuestions')}
                            </button>
                          </div>
                          )}
                              </>
                            );
                          })()}
                        </div>
                      ) : null}
                      {/* --- End Exam Hero Card --- */}
                      {currentTopicIndex !== null && topics[currentTopicIndex] ? (
                        (() => {
                          return (
                        <>
                          <h2 className="course-overview">{t('level.courseOverview')}</h2>
                          {i18n.language === 'ar' ? (
                            <p className="course-overview-desc">
                              {t('level.courseOverviewPrefix', { levelName: currentLevel.name })} <b>{getCurrentTopic()?.title}</b> {t('level.courseOverviewSuffix')}
                            </p>
                          ) : (
                            <p className="course-overview-desc">
                              {t('level.courseOverviewPrefix')} <b>{getCurrentTopic()?.title}</b> {t('level.courseOverviewSuffix', { levelName: currentLevel.name })}
                            </p>
                          )}
                          {/* ...existing code... */}
                          <div id="chapterContainer">
                            {(currentTopicContents.length === 0 && canEditSelectedTopicContent()) || (currentTopicContents.filter(item => item.is_visible).length == 0 && !canEditSelectedTopicContent() ) ? (
                              <div style={{textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)'}}>
                                <div style={{fontSize: '3rem', marginBottom: '10px'}} aria-hidden="true">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="48"
                                    height="48"
                                    viewBox="0 0 24 20"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
                                    <path d="M8 6h8M8 10h8" />
                                  </svg>
                                </div>
                                <p>{t('level.noMaterialYet')} {userIsAdmin && editorMode && t('level.clickToAddOne')}</p>
                              </div>
                            ) : (userIsAdmin || canEditSelectedTopicContent()) && editorMode ? (
                              <ReactSortable
                                list={sortableTopicContents}
                                setList={handleTopicContentReorder}
                                animation={150}
                                handle=".chapter-handle"
                                ghostClass="sortable-ghost"
                                disabled={!canEditSelectedTopicContent() || !editorMode}
                                scroll={true}
                                scrollSensitivity={60}
                                       scrollSpeed={25}
                                bubbleScroll={true}
                                forceFallback={true}
                                fallbackOnBody={true}
                                fallbackTolerance={4}
                                onEnd={suppressClickAfterDrag}
                              >
                                {
                                  sortableTopicContents.map((item, index) => {
                                  const isTest = item.type === 'test';
                                  if (isTest) {
                                    const topicTest = tests.find(t => t.topic_content === item.id);
                                    if (!topicTest) return null;
                                    const currentTestAttempt = getCurrentTestAttempt(topicTest.id);
                                    const canReviewTestAnswers = currentTestAttempt?.completed === true && !currentTestAttempt?.is_solving;
                                    return (
                                      <div key={item.id} className="chapter-card test-card" onClick={() => { if (suppressClickAfterDragRef.current) return; if (!(currentTestAttempt?.completed && !currentTestAttempt?.is_solving)) openExamModal('Start', 'test', null, item, null, currentTestAttempt); }}
                                      style={currentTestAttempt?.completed && !currentTestAttempt?.is_solving && canReviewTestAnswers ? ( currentTestAttempt?.passed ? {background: 'linear-gradient(135deg, #4ade80, #22c55e)'} : {background: 'linear-gradient(135deg, #f87171, #ef4444)'} ) : undefined}
                                      >
                                        <span className="chapter-handle" style={{display: 'inline-block', position: 'absolute', left: '10px'}}>⋮⋮</span>
                                        <div className="chapter-info" style={{marginLeft: '30px'}}>
                                          <h3 id={`chapTitle-${item.id}`} style={{fontSize: item.font_size ? `${item.font_size}px` : '20px'}}> {topicTest.title}{currentTestAttempt && currentTestAttempt.completed && (currentTestAttempt.passed ? t('level.passedSuffix') : t('level.failedSuffix'))}</h3>
                                          <p>{t('level.timeMinutes', { count: topicTest.duration })}</p>
                                        </div>
                                        <div style={{display: 'flex', gap: '10px', alignItems: 'center', paddingBottom: '2%'}}>
                                          <div className="admin-actions">
                                            { (userIsAdmin || canEditTest(topicTest.id)) && editorMode && (
                                              <button  className="review-btn"
                                                onClick={async () => {
                                                  if (!confirmDiscardUnsavedChanges()) return;
                                                  const testIndex = tests.findIndex(ts => ts.id === topicTest.id);
                                                  let testToEdit = topicTest;
                                                  // Same reasoning as the exam "Edit Questions" button above: a
                                                  // freshly-created test only exists locally (temp id) until Save
                                                  // runs, so save it first to resolve a real id before jumping into
                                                  // the question editor -- otherwise the seeded placeholder question
                                                  // has no real test to attach to and silently fails to appear.
                                                  if (isTempId(testToEdit.id)) {
                                                    const result = await saveLevelChanges();
                                                    if (!result) return;
                                                    testToEdit = result.resolvedTests[testIndex];
                                                    if (!testToEdit) return;
                                                  }
                                                  navigate(`/dashboard/${levelName}/${getCurrentTopic()?.title || item.topic?.title || ''}/tests/${testToEdit.title}`, { state: { selectedTest: testToEdit, isOnlyEditing: true } });
                                                }}
                                              >
                                                {t('level.editTest')}
                                              </button>
                                            )}
                                            <button className="action-icon btn-edit" onClick={(e) => { e.stopPropagation(); openExamModal('Update', 'test', null, item, getCurrentTopic()); }}>✎</button>
                                            <button className="action-icon btn-del" onClick={(e) => { e.stopPropagation(); openExamModal('Delete', 'test', null, item, getCurrentTopic()); }}>×</button>
                                          </div>
                                      <div className="test-score-display"> {currentTestAttempt && canReviewTestAnswers ? ((currentTestAttempt?.score || 0) + " / " + topicTest.max_score) : t('level.notSolved')}</div>
                                          {canReviewTestAnswers && (
                                            <button
                                              className="review-btn"
                                              style={{ marginLeft: '10px' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!confirmDiscardUnsavedChanges()) return;
                                                navigate(`/dashboard/${levelName}/${getCurrentTopic()?.title || item.topic?.title || ''}/tests/${topicTest.title}`, { state: { selectedTest: topicTest, isReviewing: true } });
                                              }}
                                            >
                                              {t('level.reviewTest')}
                                            </button>
                                          )}

                                          <div className="live-size-wrapper" onClick={(e) => e.stopPropagation()}>
                                            <span className="live-size-label">{t('level.sizeLabel')}</span>
                                              <input type="text" inputMode="numeric" id={`sizeInput-${item.id}`} className="live-size-input" value={item.font_size ? item.font_size : 20} placeholder={t('level.sizeDefaultPlaceholder')}
                                              onInput={(e) => updateChapterSizeLive(item.id)}></input>
                                              <div className="spinner-container">
                                                  <div className="spinner-btn up" onClick={() => adjustLiveSize(item.id, 1)}>▲</div>
                                                  <div className="spinner-btn down" onClick={() => adjustLiveSize(item.id, -1)}>▼</div>
                                              </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    const topicChapter = chapters.find(c => c.topic_content === item.id);
                                    if (!topicChapter) return null;
                                    return (
                                      <div key={item.id} className="chapter-card" onClick={() => { if (suppressClickAfterDragRef.current) return; handleNavigateToChapter(topicChapter.title); }}>
                                        <span className="chapter-handle" style={{display: 'inline-block', position: 'absolute', left: '10px'}}>⋮⋮</span>
                                        <div className="chapter-info" style={{marginLeft: '30px'}}>
                                          <h3 id={`chapTitle-${item.id}`} style={{fontSize: item.font_size ? `${item.font_size}px` : '20px'}}>{topicChapter.title}</h3>
                                          <p>{topicChapter.description}</p>
                                        </div>
                                        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                          <div className="admin-actions">
                                            <button className="action-icon btn-edit" onClick={(e) => { e.stopPropagation(); openChapterModal('Update', item); }}>✎</button>
                                            <button className="action-icon btn-del" onClick={(e) => { e.stopPropagation(); openChapterModal('Delete', item); }}>×</button>
                                          </div>
                                          <span className="chapter-icon">→</span>
                                          <div className="live-size-wrapper" onClick={(e) => e.stopPropagation()}>
                                            <span className="live-size-label">Size</span>
                                              <input type="text" inputMode="numeric" id={`sizeInput-${item.id}`} className="live-size-input" value={item.font_size ? item.font_size : 20} placeholder="20 (Def)" 
                                              onInput={(e) => updateChapterSizeLive(item.id)}></input>
                                              <div className="spinner-container">
                                                  <div className="spinner-btn up" onClick={() => adjustLiveSize(item.id, 1)}>▲</div>
                                                  <div className="spinner-btn down" onClick={() => adjustLiveSize(item.id, -1)}>▼</div>
                                              </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                })}
                              </ReactSortable>
                            ) : (
                              currentTopicContents.map((item, index) => {
                                if (!item) return null;
                                const isTest = item.type === 'test';
                                const userTopicContentProgress = userTopicContentProgresses.find(progress => progress.user === currentUser?.id && progress.topic_content === item.id);
                                const isLocked = !(userTopicContentProgress && userTopicContentProgress.unlocked) && !(userIsStaff && userIsAdmin);
                                if (isTest) {
                                  const topicTest = tests.find(t => t.topic_content === item.id);
                                  const currentTestAttempt = getCurrentTestAttempt(topicTest?.id);
                                  const canReviewTestAnswers = currentTestAttempt?.completed === true && currentTestAttempt?.is_solving === false;
                                  return (
                                    (item.is_visible || canEditSelectedTopicContent() || userIsAdmin) && (
                                      <div
                                        key={item.id}
                                        className={`chapter-card test-card ${isLocked ? 'locked-card' : ''}`}
                                        onClick={() => !isLocked && !currentTestAttempt?.completed && !currentTestAttempt?.is_solving && openExamModal('Start', 'test', null, item, null, currentTestAttempt)}
                                        style={currentTestAttempt?.completed && !currentTestAttempt?.is_solving && canReviewTestAnswers ? ( currentTestAttempt?.passed ? {background: 'linear-gradient(135deg, #4ade80, #22c55e)'} : {background: 'linear-gradient(135deg, #f87171, #ef4444)'} ) : undefined}
                                      >
                                        <div className="chapter-info">
                                          <h3 id={`chapTitle-${item.id}`} style={{fontSize: item.font_size ? `${item.font_size}px` : '20px'}}> {topicTest.title}{currentTestAttempt && canReviewTestAnswers && (currentTestAttempt.passed ? t('level.passedSuffix') : t('level.failedSuffix'))}</h3>
                                          <p>{t('level.timeMinutes', { count: topicTest.duration })}</p>
                                          {
                                            isLocked && <div className="locked-badge">{t('level.lockedCompleteFirst', { title: getPrerequisiteContentTitle(userTopicContentProgress?.prerequisite_topic_content) })}</div>
                                          }
                                        </div>
                                      <div className="test-score-display"> {currentTestAttempt && canReviewTestAnswers ? ((currentTestAttempt?.score || 0) + " / " + topicTest.max_score) : t('level.notSolved')}</div>
                                      {canReviewTestAnswers && (
                                        <button
                                          className="review-btn"
                                          style={{ marginLeft: '10px' }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/dashboard/${levelName}/${getCurrentTopic()?.title || item.topic?.title || ''}/tests/${topicTest.title}`, { state: { selectedTest: topicTest, isReviewing: true } });
                                          }}
                                        >
                                          {t('level.reviewTest')}
                                        </button>
                                      )}
                                    </div>
                                  ));
                                } else {
                                  const topicChapter = chapters.find(c => c.topic_content === item.id);
                                  if (!topicChapter) return null;
                                  return (
                                  (item.is_visible || (canEditSelectedTopicContent()) || userIsAdmin) && (
                                  <div key={item.id} className={`chapter-card ${isLocked ? 'locked-card' : ''}`} onClick={() => !isLocked && handleNavigateToChapter(topicChapter.title)}>
                                    <div className="chapter-info">
                                      <h3 id={`chapTitle-${item.id}`} style={{fontSize: item.font_size ? `${item.font_size}px` : '20px'}}>{topicChapter.title}</h3>
                                      <p>{topicChapter.description}</p>
                                        {
                                          isLocked && <div className="locked-badge">{t('level.lockedCompleteFirst', { title: getPrerequisiteContentTitle(userTopicContentProgress?.prerequisite_topic_content) })}</div>
                                        }
                                    </div>
                                    {isLocked ? <span className="chapter-icon" style={{color: 'var(--border2)'}}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="25"
                                        height="25"
                                        viewBox="0 0 24 20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-label="Locked module"
                                        role="img"
                                      >
                                        <title>Locked module</title>
                                        <rect x="3" y="10" width="18" height="11" rx="2.5" fill="currentColor" fillOpacity="0.12" />
                                        <path d="M7 10V7a5 5 0 0 1 10 0v3" />
                                        <circle cx="12" cy="15" r="1.25" fill="currentColor" stroke="none" />
                                        <path d="M12 16.25V18" />
                                        <path d="M5.5 10h13" opacity="0.45" />
                                      </svg> {/* Detailed lock icon */}
                                    </span> :
                                    <span className="chapter-icon">→</span>}
                                  </div>
                                  ))
                                }
                              })
                          )}
                          </div>
                          {(userIsAdmin || canEditSelectedTopicContent()) && editorMode && (
                            <>
                            <button
                              className="admin-btn-trigger"
                              style={{marginTop: '30px'}}
                              onClick={() => openChapterModal('Add')}
                            >
                              {t('level.addChapterTo', { topicTitle: getCurrentTopic()?.title || '' })}
                            </button>
                            <button
                              className="admin-btn-trigger"
                              style={{display: 'flex', marginTop: '10px', background: '#4c1d95'}}
                              onClick={() => openExamModal("Add", "test", null, null, getCurrentTopic())}
                            >
                                {t('level.addTest')}
                            </button>
                            </>
                          )}
                        </>
                        )
                      })())
                      : null}
                      { currentExamIndex === null && currentTopicIndex === null && (
                        <>
                          <h2 className='empty-topic'>{t('level.selectTopicToBegin')}</h2>
                          <p className='empty-topic-desc' style={{color: 'var(--text-muted)', lineHeight: '1.6'}}>{t('level.chooseTopicDesc')}</p>
                        </>
                      )}
                    </div>
                  </main>
                </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <SavingIndicator active={isSavingLevel} label={t('common.saving')} />
      <Toast toast={toast} />
    </>
  );
}

export default Level;
